/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */

import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';
import { RoverBrand } from '@rover/types/src/RoverBrand';

import { ActivationStatusEnum, MigratedUserStatus } from './types';

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks page view of the Brand Activation page, fires every time the stage changes
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationPageView extends AnalyticsEvent {
  eventName = 'brand-activation-page-view';

  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      stage: ActivationStatusEnum;
      migratedUserStatus: MigratedUserStatus;
      isProvider: boolean;
    },
    observabilityTags: {
      stage: ActivationStatusEnum;
      migratedUserStatus: MigratedUserStatus;
      isProvider: boolean;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks Terms of Service checkbox clicks
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationTOSCheckboxClick extends AnalyticsEvent {
  eventName = 'brand-activation-tos-checkbox-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(eventProperties: { brand: RoverBrand; checked: boolean }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks marketing opt-in checkbox clicks
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationMarketingCheckboxClick extends AnalyticsEvent {
  eventName = 'brand-activation-marketing-checkbox-click';

  consentPurpose: ConsentPurpose = 'marketing';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(eventProperties: { brand: RoverBrand; checked: boolean }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks accept button click before API call
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationAcceptButtonClick extends AnalyticsEvent {
  eventName = 'brand-activation-accept-button-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      agreesToTOS: boolean;
      agreesToMarketing: boolean;
    },
    observabilityTags?: Record<string, unknown>
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks "Finish my profile setup" button click (providers only)
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationProfileButtonClick extends AnalyticsEvent {
  eventName = 'brand-activation-profile-button-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      flowSource: string;
    },
    observabilityTags: {
      flowSource: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks "Search for pet care" button click (owners only)
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationSearchButtonClick extends AnalyticsEvent {
  eventName = 'brand-activation-search-button-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      flowSource: string;
    },
    observabilityTags: {
      flowSource: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks "View my bookings" button click (both user types)
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationInboxButtonClick extends AnalyticsEvent {
  eventName = 'brand-activation-inbox-button-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      flowSource: string;
    },
    observabilityTags: {
      flowSource: string;
    }
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks FAQ category filter selection
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */
export class BrandActivationFAQCategoryClick extends AnalyticsEvent {
  eventName = 'brand-activation-faq-category-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(
    eventProperties: {
      brand: RoverBrand;
      categoryId: string;
      categoryLabel: string;
    },
    observabilityTags?: Record<string, unknown>
  ) {
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Tracks help center link clicks
 * For more context, check the RFC:
 * https://roverdotcom.atlassian.net/wiki/spaces/TEAM/pages/5200937755/RFC+User+Activation+Experience
 *
 * &nbsp;
 */

export class BrandActivationFAQHelpCenterClick extends AnalyticsEvent {
  eventName = 'brand-activation-faq-help-center-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.marketing.m_and_a;

  constructor(eventProperties: { brand: RoverBrand }) {
    super(eventProperties);
  }
}
